import React, { useState, useEffect } from "react";
import { Tab } from "@headlessui/react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Pie, Bar } from "react-chartjs-2";
import Modal from "../../../components/shared/Modal";
import SubPageLayout from "../../../components/shared/SubPageLayout";
import OnboardingIntake from "./OnboardingIntake";
import ClientProfiles from "./ClientProfiles";
import Planning from "./Planning";
import AIAssistant from "./AIAssistant";
import Resources from "./Resources";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
);

const EstatePlanning = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [activeQuickAction, setActiveQuickAction] = useState(null);
  const [estateMetrics, setEstateMetrics] = useState({
    totalEstateValue: 117050000,
    completedPlans: 65,
    inProgressPlans: 25,
    unstartedPlans: 10,
    clientDemographics: {
      ageGroups: {
        "Under 40": 15,
        "40-60": 45,
        "Over 60": 40,
      },
      familyStructures: {
        Single: 20,
        Married: 60,
        Divorced: 15,
        Widowed: 5,
      },
      assetTypes: {
        "Real Estate": 35,
        Investments: 40,
        Business: 15,
        Other: 10,
      },
    },
  });

  const [quickActions, setQuickActions] = useState([
    {
      id: 1,
      title: "New Estate Plan",
      icon: "📝",
      action: () => setActiveQuickAction("newPlan"),
      description: "Start a new estate plan for a client",
    },
    {
      id: 2,
      title: "Share Intake Form",
      icon: "📋",
      action: () => setActiveQuickAction("shareForm"),
      description: "Send an intake form to a new or existing client",
    },
    {
      id: 3,
      title: "Schedule Meeting",
      icon: "📅",
      action: () => setActiveQuickAction("scheduleMeeting"),
      description: "Schedule a meeting with a client or team member",
    },
  ]);

  const pieChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "bottom",
      },
    },
  };

  const planStatusData = {
    labels: ["Completed", "In Progress", "Not Started"],
    datasets: [
      {
        data: [
          estateMetrics.completedPlans,
          estateMetrics.inProgressPlans,
          estateMetrics.unstartedPlans,
        ],
        backgroundColor: ["#4CAF50", "#FFA726", "#EF5350"],
      },
    ],
  };

  const assetDistributionData = {
    labels: Object.keys(estateMetrics.clientDemographics.assetTypes),
    datasets: [
      {
        data: Object.values(estateMetrics.clientDemographics.assetTypes),
        backgroundColor: ["#2196F3", "#9C27B0", "#FF9800", "#607D8B"],
      },
    ],
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Top Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Total Estate Value
          </h3>
          <p className="text-2xl font-bold text-[#202EC6]">
            ${estateMetrics.totalEstateValue.toLocaleString()}
          </p>
        </div>
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Completed Plans</h3>
          <p className="text-2xl font-bold text-green-600">
            {estateMetrics.completedPlans}%
          </p>
        </div>
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">In Progress</h3>
          <p className="text-2xl font-bold text-orange-500">
            {estateMetrics.inProgressPlans}%
          </p>
        </div>
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Not Started</h3>
          <p className="text-2xl font-bold text-red-500">
            {estateMetrics.unstartedPlans}%
          </p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Plan Status Distribution
          </h3>
          <div className="h-64">
            <Pie data={planStatusData} options={pieChartOptions} />
          </div>
        </div>
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Asset Type Distribution
          </h3>
          <div className="h-64">
            <Pie data={assetDistributionData} options={pieChartOptions} />
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Quick Actions
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {quickActions.map((action) => (
            <button
              key={action.id}
              onClick={() => setActiveQuickAction(`newPlan${action.id}`)}
              className="flex items-center justify-center space-x-2 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <span className="text-2xl">{action.icon}</span>
              <span className="text-gray-900 dark:text-gray-100 font-medium">{action.title}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderOnboarding = () => <OnboardingIntake />;

  const renderClientProfiles = () => <ClientProfiles />;

  const renderPlanning = () => <Planning />;

  const renderAIAssistant = () => <AIAssistant />;

  const renderResources = () => <Resources />;

  const renderQuickActionModal = () => {
    if (!activeQuickAction) return null;

    const actionId = parseInt(activeQuickAction.replace(/\D/g, ""));
    const action = quickActions.find((a) => a.id === actionId);
    if (!action) return null;

    return (
      <Modal isOpen={!!activeQuickAction} onClose={() => setActiveQuickAction(null)} maxWidth="md">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-2xl mr-2">{action.icon}</span>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {action.title}
              </h3>
            </div>
            <button
              onClick={() => setActiveQuickAction(null)}
              className="text-gray-400 hover:text-gray-500 dark:text-gray-500"
            >
              <span className="sr-only">Close</span>
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
        <div className="px-6 py-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">{action.description}</p>
          {actionId === 1 && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Select Client
              </label>
              <select className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-neutral-800 text-gray-900 dark:text-gray-100">
                <option value="">Choose a client</option>
                <option value="1">John & Sarah Smith</option>
                <option value="2">Robert Johnson</option>
                <option value="3">Emily & Michael Davis</option>
              </select>
            </div>
          )}
          {actionId === 2 && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Client Email
              </label>
              <input
                type="email"
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-neutral-800 text-gray-900 dark:text-gray-100"
                placeholder="Enter client email"
              />
            </div>
          )}
          {actionId === 3 && (
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Select Client
                </label>
                <select className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-neutral-800 text-gray-900 dark:text-gray-100">
                  <option value="">Choose a client</option>
                  <option value="1">John & Sarah Smith</option>
                  <option value="2">Robert Johnson</option>
                  <option value="3">Emily & Michael Davis</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Meeting Date
                </label>
                <input
                  type="datetime-local"
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-neutral-800 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>
          )}
        </div>
        <div className="px-6 py-4 bg-gray-50 dark:bg-neutral-900 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
          <button
            onClick={() => setActiveQuickAction(null)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-neutral-800"
          >
            Cancel
          </button>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700">
            {actionId === 1
              ? "Create Plan"
              : actionId === 2
                ? "Send Form"
                : "Schedule Meeting"}
          </button>
        </div>
      </Modal>
    );
  };

  return (
    <SubPageLayout
      breadcrumbItems={[
        { label: "Financial Planning", path: "/financial-planning" },
        {
          label: "Estate Planning",
          path: "/financial-planning/estate-planning",
        },
      ]}
      title="Estate Planning"
      subtitle="Manage and track estate planning activities for your clients"
    >
      <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
        <Tab.List className="flex space-x-1 rounded-xl bg-blue-900/20 p-1 mb-6">
          {[
            "Dashboard",
            "Onboarding & Intake",
            "Client Profiles",
            "Planning",
            "AI Assistant",
            "Resources",
          ].map((tab) => (
            <Tab
              key={tab}
              className={({ selected }) =>
                `w-full rounded-lg py-2.5 text-sm font-medium leading-5
                ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2
                ${
                  selected
                    ? "bg-surface-default dark:bg-surface-default shadow text-blue-700"
                    : "text-gray-900 dark:text-gray-100 hover:bg-surface-default dark:bg-surface-default/[0.12]"
                }`
              }
            >
              {tab}
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels>
          <Tab.Panel>{renderDashboard()}</Tab.Panel>
          <Tab.Panel>{renderOnboarding()}</Tab.Panel>
          <Tab.Panel>{renderClientProfiles()}</Tab.Panel>
          <Tab.Panel>{renderPlanning()}</Tab.Panel>
          <Tab.Panel>{renderAIAssistant()}</Tab.Panel>
          <Tab.Panel>{renderResources()}</Tab.Panel>
        </Tab.Panels>
      </Tab.Group>

      {renderQuickActionModal()}
    </SubPageLayout>
  );
};

export default EstatePlanning;
